import { useEffect, useState, useCallback } from 'react';
import { showSpinner, hideSpinner } from '@/components/Layout/LoadingSpinner';
import { trpc } from '@/api/trpc';
import { handleTrpcQuery } from '@/api/trpcResponseHandler';
import { useAppQuery } from '@/hooks/useAppQuery';
import { useAssetSessionPrices } from '@/hooks/useAssetSessionPrices';
import AssetSessionPriceDisplay from '@/components/Market/AssetSessionPriceDisplay';
import { DEFAULT_REQUISITIES_STALE_TIME } from '@/config/constants';

import type { EntityTypeUnion } from '@einhaus/project314-types';
import ChartContainer from '../Chart/ChartContainer';
import { abbreviateNumber } from '@/utils/helpers';
import { useAuth } from '../../context/AuthContext.tsx';

interface EntityProps {
    entityId: number;
    entityType: EntityTypeUnion['entityType'];
    onDataLoaded?: (data: { name: string }) => void;
}

const Entity = ({ entityId, entityType, onDataLoaded }: EntityProps) => {
    const [categoryMap, setCategoryMap] = useState<Record<string, string>>({});
    const [entityData, setEntityData] = useState<EntityTypeUnion | null>(null);
    const { isAuthenticated } = useAuth();

    const [entityPriceData, setEntityPriceData] = useState<{
        changeNumber: number;
        currency: string;
        percentChange: number;
        price: number;
    }>({
        changeNumber: 0,
        currency: 'USD',
        percentChange: 0,
        price: 0
    });

    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const sessionPricesQuery = useAssetSessionPrices(entityType === 'Asset' && entityData ? [entityData.symbol] : []);

    // Fetch categories for mapping IDs to names
    const mappingsQuery = useAppQuery({
        ...trpc.mappings.fetchMappings.queryOptions(),
        staleTime: DEFAULT_REQUISITIES_STALE_TIME
    });

    // Handle mappings query errors and success
    useEffect(() => {
        if (mappingsQuery.error) {
            handleTrpcQuery(mappingsQuery, {
                fallbackErrorMessage: 'Failed to fetch category mappings',
                showErrorToast: true,
                setError: (msg) => console.error(`Error fetching categories: ${msg}`)
            });
        } else if (mappingsQuery.data?.assetCategories) {
            const categoryMapping: Record<string, string> = {};

            mappingsQuery.data.assetCategories.forEach((category: { id?: number; name?: string }) => {
                if (category.id && category.name) {
                    categoryMapping[category.id.toString()] = category.name;
                }
            });

            setCategoryMap(categoryMapping);
        }
        // Only depend on the data and error properties, not the entire query object
    }, [mappingsQuery.data, mappingsQuery.error]);

    // Fetch entity details using tRPC
    const entityDetailsQuery = useAppQuery({
        ...trpc.entity.details.queryOptions({
            entityId,
            entityType
        }),
        enabled: !!entityId
    });

    // Handle entity details loading state
    useEffect(() => {
        if (entityDetailsQuery.isLoading) {
            showSpinner();
        }
    }, [entityDetailsQuery.isLoading]);

    // Handle entity details error and success
    const processEntityDetails = useCallback(() => {
        if (entityDetailsQuery.error) {
            handleTrpcQuery(entityDetailsQuery, {
                fallbackErrorMessage: `Failed to load ${entityType} data`,
                showErrorToast: true,
                setError: (msg) => setError(`Failed to load ${entityType} data. Please try again later. ${msg}`)
            });

            setLoading(false);
            hideSpinner();
        } else if (entityDetailsQuery.data?.entity) {
            const entityResponse = entityDetailsQuery.data.entity;
            setEntityData(entityResponse);

            if (onDataLoaded) {
                onDataLoaded({ name: entityResponse.name || '' });
            }

            setError(null);
            setLoading(false);
            hideSpinner();
        }
    }, [entityDetailsQuery.data, entityDetailsQuery.error, entityType, onDataLoaded]);

    useEffect(() => {
        processEntityDetails();
    }, [processEntityDetails]);

    // Fetch entity price using tRPC query
    const entityPriceQuery = useAppQuery({
        ...trpc.entity.fetchEntityPrice.queryOptions({
            entityId,
            entityType
        }),
        enabled: !!entityId && !!entityData && entityType !== 'Asset',
        // Restore automatic refreshing every 10 seconds
        refetchInterval: 10000
    });

    // Handle loading states for price data
    useEffect(() => {
        if (entityPriceQuery.isLoading && !entityDetailsQuery.isLoading) {
            showSpinner();
        } else if (!entityDetailsQuery.isLoading) {
            hideSpinner();
        }
    }, [entityPriceQuery.isLoading, entityDetailsQuery.isLoading]);

    // Handle errors for price data
    const handlePriceError = useCallback(() => {
        // Don't show price errors on non logged in, we block it to prevent bots from hotting TradeStation/Schwab quotes
        if (entityPriceQuery.error && isAuthenticated) {
            handleTrpcQuery(entityPriceQuery, {
                fallbackErrorMessage: `Failed to get price data for ${entityType}`,
                showErrorToast: true,
                setError: (msg) => console.error(`Error fetching price: ${msg}`)
            });
        }
    }, [entityPriceQuery.error, entityType]);

    useEffect(() => {
        handlePriceError();
    }, [handlePriceError]);

    // Process price data when it changes
    const processPriceData = useCallback(() => {
        if (!entityPriceQuery.data || !entityData) return;

        const priceData = entityPriceQuery.data;

        const currentPrice = priceData.price || 0;
        let referencePrice = 0;
        let changeNumber = 0;
        let percentChange = 0;
        let currency = 'USD';

        if (entityType === 'Cryptocurrency') {
            referencePrice = entityData.openingPrice || 0;
            currency = 'USD';
        } else if ((entityType === 'Asset' || entityType === 'AssetIndex') && 'lastClosePrice' in entityData) {
            referencePrice = entityData.lastClosePrice || 0;

            if ('currency' in entityData && entityData.currency) {
                ({ currency } = entityData);
            }
        }

        if (referencePrice && currentPrice) {
            changeNumber = currentPrice - referencePrice;
            percentChange = (changeNumber / referencePrice) * 100;
        } else {
            console.warn('Could not calculate change; missing prices:', {
                currentPrice,
                referencePrice
            });
        }

        setEntityPriceData({
            changeNumber,
            currency,
            percentChange,
            price: currentPrice
        });
    }, [entityPriceQuery.data, entityType, entityData]);

    useEffect(() => {
        processPriceData();
    }, [processPriceData]);

    if (loading) {
        return <div>Loading...</div>;
    }

    if (error) {
        return <div className="error-message">{error}</div>;
    }

    if (!entityData) {
        return <div>No data found for this entity.</div>;
    }

    return (
        <div className="w-full">
            {/* Entity Header */}
            <div className="grid grid-cols-12 items-start p-3 mb-1">
                <div className="col-span-12 md:col-span-6">
                    <h2 className="text-3xl font-bold mb-0">{entityData.name}</h2>
                    {entityData.symbol && <div className="text-lg mb-1 text-[#0275d8] font-bold">{entityData.symbol}</div>}
                    {/* Categories */}
                    {entityType === 'Asset' && 'categories' in entityData && entityData.categories && entityData.categories.length > 0 && (
                        <div className="mb-3">
                            {entityData.categories.map((categoryId) => (
                                <span className="bg-zinc-600 mr-1 text-white px-2 py-1 rounded-full text-xs" key={categoryId}>
                                    {categoryMap[categoryId] || `Category ${categoryId}`}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
                <div className="col-span-12 md:col-span-6 md:text-right">
                    {entityType === 'Asset' ? (
                        <div className="mt-3 inline-block md:mt-0">
                            <AssetSessionPriceDisplay
                                prices={sessionPricesQuery.data?.find((item) => item.assetId === entityId)}
                                isLoading={sessionPricesQuery.isLoading}
                                isError={sessionPricesQuery.isError}
                            />
                        </div>
                    ) : (
                        <div className="flex items-baseline md:justify-end">
                            <div className="text-2xl mr-2">
                                {entityPriceData.currency === 'USD' ? '$' : ''}
                                {entityPriceData.price.toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                })}
                                {entityPriceData.currency !== 'USD' ? ` ${entityPriceData.currency}` : ''}
                            </div>
                            <div
                                className={`ml-2 mr-1 text-lg ${entityPriceData.changeNumber > 0 ? 'text-profit-green' : entityPriceData.changeNumber < 0 ? 'text-red-500' : ''}`}
                            >
                                {entityPriceData.changeNumber > 0 ? '+' : ''}
                                {entityPriceData.changeNumber.toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                })}
                            </div>
                            <div
                                className={`ml-1 text-lg ${entityPriceData.percentChange > 0 ? 'text-profit-green' : entityPriceData.percentChange < 0 ? 'text-red-500' : ''}`}
                            >
                                ({entityPriceData.percentChange > 0 ? '+' : ''}
                                {entityPriceData.percentChange.toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                })}
                                %)
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Chart Components */}
            {entityData && !loading && (
                <div className="w-full">
                    <div className="w-full min-h-[500px] h-auto mb-8" style={{ display: 'flex', flexDirection: 'column' }}>
                        <ChartContainer entity={entityData} key={`${entityData.entityType}:${entityData.id}`} />
                    </div>
                </div>
            )}

            {/* Display entity-specific information */}
            <div className="grid grid-cols-12 mt-1">
                <div className="col-span-12">
                    <div className="flex flex-wrap gap-3">
                        {/* Shard ID */}
                        {entityData.shardId !== undefined && (
                            <div className="p-3 rounded" style={{ backgroundColor: '#2a2e32' }}>
                                <div className="font-bold text-zinc-400 mb-1">Shard ID</div>
                                <div>{entityData.shardId}</div>
                            </div>
                        )}

                        {/* Market Cap */}
                        {entityData.marketCap !== undefined && entityData.marketCap !== null && (
                            <div className="p-3 rounded" style={{ backgroundColor: '#2a2e32' }}>
                                <div className="font-bold text-zinc-400 mb-1">Market Cap</div>
                                <div>{abbreviateNumber(entityData.marketCap, 2, false, false)}</div>
                            </div>
                        )}

                        {/* Asset-specific fields */}
                        {entityType === 'Asset' && (
                            <>
                                {'advancedStats' in entityData && entityData.advancedStats?.peRatio !== undefined && (
                                    <div className="p-3 rounded" style={{ backgroundColor: '#2a2e32' }}>
                                        <div className="font-bold text-zinc-400 mb-1">PE Ratio</div>
                                        <div>{entityData.advancedStats.peRatio ? entityData.advancedStats.peRatio.toFixed(2) : '-'}</div>
                                    </div>
                                )}

                                {'advancedStats' in entityData && entityData.advancedStats?.shortPercentFloat !== undefined && (
                                    <div className="p-3 rounded" style={{ backgroundColor: '#2a2e32' }}>
                                        <div className="font-bold text-zinc-400 mb-1">Short % Float</div>
                                        <div>
                                            {entityData.advancedStats.shortPercentFloat
                                                ? `${(entityData.advancedStats.shortPercentFloat * 100).toFixed(2)}%`
                                                : '-'}
                                        </div>
                                    </div>
                                )}

                                {'dividendYield' in entityData && entityData.dividendYield !== undefined && (
                                    <div className="p-3 rounded" style={{ backgroundColor: '#2a2e32' }}>
                                        <div className="font-bold text-zinc-400 mb-1">Dividend Yield</div>
                                        <div>{entityData.dividendYield ? `${(entityData.dividendYield * 100).toFixed(2)}%` : '-'}</div>
                                    </div>
                                )}
                            </>
                        )}

                        {/* Cryptocurrency-specific fields */}
                        {entityType === 'Cryptocurrency' && (
                            <>
                                {'circulatingSupply' in entityData && entityData.circulatingSupply !== undefined && (
                                    <div className="p-3 rounded" style={{ backgroundColor: '#2a2e32' }}>
                                        <div className="font-bold text-zinc-400 mb-1">Circulating Supply</div>
                                        <div>{entityData.circulatingSupply?.toLocaleString()}</div>
                                    </div>
                                )}

                                {'totalSupply' in entityData && entityData.totalSupply !== undefined && (
                                    <div className="p-3 rounded" style={{ backgroundColor: '#2a2e32' }}>
                                        <div className="font-bold text-zinc-400 mb-1">Total Supply</div>
                                        <div>{entityData.totalSupply?.toLocaleString()}</div>
                                    </div>
                                )}

                                {'maxSupply' in entityData && entityData.maxSupply !== undefined && (
                                    <div className="p-3 rounded" style={{ backgroundColor: '#2a2e32' }}>
                                        <div className="font-bold text-zinc-400 mb-1">Max Supply</div>
                                        <div>{entityData.maxSupply?.toLocaleString()}</div>
                                    </div>
                                )}
                            </>
                        )}

                        {entityData.entityMonitorNode && (
                            <div className="p-3 rounded" style={{ backgroundColor: '#2a2e32' }}>
                                <div className="font-bold text-zinc-400 mb-1">Entity Monitor Node</div>
                                <div>{entityData.entityMonitorNode}</div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Entity;
